import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { TenantService } from '../services/tenantService';

export interface TenantInfo {
  id: string;
  name: string;
  email: string;
  subscriptionTier: 'free' | 'pro' | 'enterprise';
  isAccessRestricted: boolean;
  userCount: number;
  messageCount: number;
  status: 'active' | 'suspended' | 'pending';
  createdAt: string;
  lastActiveAt: string;
}

export interface TenantUser {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'member';
  status: 'active' | 'pending';
  isActive: boolean;
  lastLoginAt?: string;
}

interface TenantContextType {
  tenant: TenantInfo | null;
  users: TenantUser[];
  loading: boolean;
  error: string | null;
  fetchTenant: () => Promise<void>;
  fetchUsers: () => Promise<void>;
  updateTenantInfo: (data: Partial<TenantInfo>) => Promise<void>;
  addUser: (email: string, name: string, role: TenantUser['role']) => Promise<void>;
  removeUser: (userId: string) => Promise<void>;
  updateUserRole: (userId: string, role: TenantUser['role']) => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tenant, setTenant] = useState<TenantInfo | null>(null);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTenant = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await TenantService.getTenantInfo();
      setTenant(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tenant info');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await TenantService.getTenantUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateTenantInfo = useCallback(async (data: Partial<TenantInfo>) => {
    setLoading(true);
    setError(null);
    try {
      const updated = await TenantService.updateTenant(data);
      setTenant(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update tenant');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const addUser = useCallback(async (email: string, name: string, role: TenantUser['role']) => {
    setLoading(true);
    setError(null);
    try {
      const newUser = await TenantService.inviteUser({ email, name, role });
      setUsers([...users, newUser]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add user');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [users]);

  const removeUser = useCallback(async (userId: string) => {
    setLoading(true);
    setError(null);
    try {
      await TenantService.removeUser(userId);
      setUsers(users.filter(u => u.id !== userId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove user');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [users]);

  const updateUserRole = useCallback(async (userId: string, role: TenantUser['role']) => {
    setLoading(true);
    setError(null);
    try {
      const updated = await TenantService.updateUserRole(userId, role);
      setUsers(users.map(u => u.id === userId ? updated : u));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user role');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [users]);

  // Load tenant info on mount
  useEffect(() => {
    fetchTenant();
  }, [fetchTenant]);

  const value: TenantContextType = {
    tenant,
    users,
    loading,
    error,
    fetchTenant,
    fetchUsers,
    updateTenantInfo,
    addUser,
    removeUser,
    updateUserRole,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (!context) {
    // Return safe defaults during render transitions before TenantProvider mounts
    return {
      tenant: null,
      users: [],
      loading: true,
      error: null,
      fetchTenant: async () => {},
      fetchUsers: async () => {},
      updateTenantInfo: async () => {},
      addUser: async () => {},
      removeUser: async () => {},
      updateUserRole: async () => {},
    };
  }
  return context;
};
